// Carried-price provenance — P3-C (docs/implementation/P3_QUOTES_PRICING_INTELLIGENCE.md
// Phase F). A manual estimate line (EstimateCostLineItems, db/30) prices off
// its catalog Item/Assembly by default; the estimator may instead CARRY a
// price from evidence, and the line records where it came from:
//
//   CostSource      'catalog' (or NULL) — the entity's own cost, live
//                   'quote'             — a project quote line (QuoteID + QuoteLineItemID)
//                   'manual'            — an estimator-entered unit cost
//   SourceUnitCost  the evidence's unit cost at the moment it was carried
//                   (a quote line's UnitPrice; for 'manual', the catalog cost
//                   then) — frozen, so a later catalog/quote change never
//                   silently moves a priced revision
//   CarriedUnitCost the DIRECT unit cost actually priced (before sales burden
//                   / markup). Adjustment = CarriedUnitCost − SourceUnitCost.
//   CarriedNote     why (e.g. "+ freight, quote excludes it")
//
// Evidence chain rule: nothing is overwritten. The quote keeps its price, the
// catalog keeps its cost, the line keeps what was carried and from where.
// Takeoff-mapped rollup rows have no per-revision row to carry a price on —
// a per-revision catalog override is a documented future slice.

const num = (v) => { if (v === null || v === undefined || v === "") return null; const n = Number(v); return Number.isFinite(n) ? n : null; };

export const COST_SOURCES = [
  { key: "catalog", label: "Catalog cost",   description: "The Item/Assembly's own material + labor + vendor cost, live from Setup ▸ Estimate Engine." },
  { key: "quote",   label: "Quote line",     description: "A line from one of this project's quotes; its unit price is frozen on the estimate line." },
  { key: "manual",  label: "Manual",         description: "A unit cost the estimator entered, with a note on why." },
];
export const COST_SOURCE_KEYS = COST_SOURCES.map(s => s.key);

// The entity's own direct unit cost (material + labor × factor × rate + vendor).
export function catalogUnitCost(entity, laborFactor, settings) {
  if (!entity) return 0;
  const factorMult = laborFactor?.Multiplier ?? 1;
  return (num(entity.MaterialCostPerUnit) || 0) + (num(entity.LaborHrPerUnit) || 0) * factorMult * (num(settings?.LaborRate) || 0) + (num(entity.VendorSubCostPerUnit) || 0);
}

// Which direct unit cost a line prices at, and its basis.
//   { unitCost, source, carried, catalogUnitCost, sourceUnitCost, carriedUnitCost, adjustment, stale }
//   stale: a 'quote' line whose SourceUnitCost no longer equals the live quote line's UnitPrice (needs the joined SourceLineUnitPrice)
export function resolveLineUnitCost(line, entity, laborFactor, settings) {
  const catalog = catalogUnitCost(entity, laborFactor, settings);
  const source = line?.CostSource && line.CostSource !== "catalog" ? line.CostSource : "catalog";
  const carried = num(line?.CarriedUnitCost);
  if (source === "catalog" || carried === null) {
    return { unitCost: catalog, source: "catalog", carried: false, catalogUnitCost: catalog, sourceUnitCost: null, carriedUnitCost: null, adjustment: null, stale: false };
  }
  const sourceUnit = num(line.SourceUnitCost);
  const liveQuoteUnit = num(line.SourceLineUnitPrice);
  return {
    unitCost: carried, source, carried: true, catalogUnitCost: catalog,
    sourceUnitCost: sourceUnit, carriedUnitCost: carried,
    adjustment: sourceUnit === null ? null : Math.round((carried - sourceUnit) * 100) / 100,
    stale: source === "quote" && sourceUnit !== null && liveQuoteUnit !== null && Math.abs(liveQuoteUnit - sourceUnit) > 0.005,
  };
}

// One-line basis text for the estimate table ("Quote: ASI Doors 2026-09-01 @ $1,250.00 +$50.00 adj").
export function describeBasis(line, resolved) {
  if (!resolved?.carried) return "Catalog cost (live)";
  const money = (v) => v == null ? "—" : `$${(+v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const adj = resolved.adjustment ? ` ${resolved.adjustment > 0 ? "+" : "−"}${money(Math.abs(resolved.adjustment))} adj` : "";
  if (resolved.source === "quote") {
    const who = [line.SourceVendorName, line.SourceQuoteDate ? String(line.SourceQuoteDate).slice(0, 10) : null].filter(Boolean).join(" ");
    return `Quote${who ? `: ${who}` : ` #${line.QuoteID}`} @ ${money(resolved.sourceUnitCost)}${adj}${resolved.stale ? " (quote line changed since)" : ""}`;
  }
  return `Manual @ ${money(resolved.carriedUnitCost)}${resolved.sourceUnitCost != null ? ` (catalog was ${money(resolved.sourceUnitCost)})` : ""}${line.CarriedNote ? ` — ${line.CarriedNote}` : ""}`;
}

// Quote-line picker options from sp_GetProjectQuotes' Table1 (quotes) + Table2 (lines).
export function quoteLineOptions(quotes = [], lines = []) {
  const byId = new Map(quotes.map(q => [q.QuoteID, q]));
  return lines
    .filter(l => byId.has(l.QuoteID) && num(l.UnitPrice) !== null)
    .map(l => {
      const q = byId.get(l.QuoteID);
      return {
        quoteId: l.QuoteID, quoteLineItemId: l.LineItemID, unitPrice: num(l.UnitPrice),
        vendorName: q.VendorName || null, quoteDate: q.QuoteDate || null, category: q.Category || null,
        label: `${q.VendorName || "no vendor"} · ${q.Category || ""}${q.QuoteDate ? ` · ${String(q.QuoteDate).slice(0, 10)}` : ""} — #${l.LineNumber ?? ""} ${l.Description || ""} @ $${num(l.UnitPrice).toLocaleString("en-US", { minimumFractionDigits: 2 })}${l.Unit ? `/${l.Unit}` : ""}`.replace(/\s+·\s+·/g, " ·"),
      };
    });
}

// sp_SaveEstimateCostLineItem carried-pricing keys. The proc writes them ONLY
// when present, so a save that isn't about pricing (an alternate move) can
// omit them entirely and leave the basis untouched.
//   mode 'catalog' -> clears every provenance field
//   mode 'quote'   -> { quoteId, quoteLineItemId, sourceUnitCost, carriedUnitCost (defaults to source), note }
//   mode 'manual'  -> { carriedUnitCost (required), sourceUnitCost (the catalog cost now), note }
export function carriedPricingPayload({ mode, quoteId = null, quoteLineItemId = null, sourceUnitCost = null, carriedUnitCost = null, note = null } = {}) {
  if (mode === "catalog" || !mode) return { cost_source: "catalog", quote_id: null, quote_line_item_id: null, source_unit_cost: null, carried_unit_cost: null, carried_note: null };
  if (mode === "quote") {
    if (quoteId == null) throw new Error("carriedPricingPayload: a quote line source needs quoteId");
    const src = num(sourceUnitCost);
    return { cost_source: "quote", quote_id: quoteId, quote_line_item_id: quoteLineItemId ?? null, source_unit_cost: src, carried_unit_cost: num(carriedUnitCost) ?? src, carried_note: note || null };
  }
  if (mode === "manual") {
    const carried = num(carriedUnitCost);
    if (carried === null) throw new Error("carriedPricingPayload: a manual source needs carriedUnitCost");
    return { cost_source: "manual", quote_id: null, quote_line_item_id: null, source_unit_cost: num(sourceUnitCost), carried_unit_cost: carried, carried_note: note || null };
  }
  throw new Error(`carriedPricingPayload: unknown mode "${mode}"`);
}
